// Age-group helpers — kid-friendly difficulty bucketing.
// 3 groups: little (4-6, tier=seedlings) / big (6-10, tier=explorers) /
// older (10-15, tier=pioneers). Legacy `id`s ("little"/"big"/"older")
// remain the on-disk keys for backward compatibility — every existing
// user's localStorage silently migrates by keeping the same id but
// picking up the new `tier`, `ageRange` labels and gating flags.

import { useEffect, useState } from "react";

const KEY = "we_age_group";
const SEEN_KEY = "we_age_seen";

/**
 * @typedef {Object} AgeGroup
 * @property {"little"|"big"|"older"} id     Stable localStorage key
 * @property {"seedlings"|"explorers"|"pioneers"} tier  Montessori-style label
 * @property {string} label       Public tier name (uppercase display)
 * @property {string} ageRange    Human-readable age band
 * @property {number} defaultAge  Representative numeric age (used by content tiers)
 * @property {string} emoji       Nature icon per tier
 * @property {string} color       Tailwind bg-* accent
 * @property {string} description Short child-facing tagline (no age numbers)
 * @property {boolean} hasStreaks Whether streak counters render for this tier
 * @property {boolean} hasQuizzes Whether unprompted quizzes / test CTAs render
 * @property {string} recommendStart  Landing route hint
 * @property {string[]} recommendedTiles  Home tile priority
 */

export const AGE_GROUPS = [
  {
    id: "little",
    tier: "seedlings",
    label: "Seedlings",
    ageRange: "4–6",
    band: "4–6",
    defaultAge: 5,
    emoji: "🌱",
    color: "bg-rose-300",
    description: "Sensory wonder — big pictures, sounds and colour",
    hasStreaks: false,
    hasQuizzes: false,
    recommendStart: "/",
    recommendedTiles: ["learn", "color", "puzzles", "games", "stickers"],
  },
  {
    id: "big",
    tier: "explorers",
    label: "Explorers",
    ageRange: "7–10",
    band: "7–10",
    defaultAge: 8,
    emoji: "🚀",
    color: "bg-yellow-300",
    description: "Cosmic connections — the whole world as one story",
    hasStreaks: true,
    hasQuizzes: true,
    recommendStart: "/",
    recommendedTiles: ["globe", "continents", "math", "trip", "quiz", "games"],
  },
  {
    id: "older",
    tier: "pioneers",
    label: "Pioneers",
    ageRange: "11–15",
    band: "11–15",
    defaultAge: 12,
    emoji: "🌍",
    color: "bg-emerald-300",
    description: "Depth and purpose — real questions, real learning",
    hasStreaks: true,
    hasQuizzes: true,
    recommendStart: "/",
    recommendedTiles: ["advanced", "quiz", "math", "trip"],
  },
];

export function getAgeGroup() {
  try {
    return localStorage.getItem(KEY); // 'little' | 'big' | 'older' | null
  } catch {
    return null;
  }
}

// Returns true once the splash has been seen (either chosen or skipped).
// Used to make sure the splash is strictly one-time-only — even if the age
// group value is later cleared, the user is never re-prompted automatically.
// They can still re-open it via the header age chip.
export function getAgeSeen() {
  try {
    return localStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

export function setAgeSeen() {
  try {
    localStorage.setItem(SEEN_KEY, "1");
  } catch {
    // ignore quota
  }
}

export function setAgeGroup(id) {
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent("we-age-changed", { detail: { id } }));
  } catch {
    // ignore quota
  }
}

export function getAgeMeta(id) {
  return AGE_GROUPS.find((g) => g.id === id) || null;
}

// Age-suitability mapping for difficulty pickers across the app.
// Each game keys a list of difficulty ids and the age groups they're meant
// for. Items NOT included for the current group are dimmed + locked with a
// "✏️ Change age" hint — never hidden, so kids see what's possible later.
// Keys are intentionally string-stable so adding new games is additive.
//
// Convention: an empty / missing entry === suitable for all ages.
const SUITABILITY = {
  // Maths Academy years 1-7
  maths: {
    1: ["little", "big", "older"],
    2: ["little", "big", "older"],
    3: ["little", "big", "older"],
    4: ["big", "older"],
    5: ["big", "older"],
    6: ["older"],
    7: ["older"],
  },
  // Spell difficulty
  spell: {
    easy:   ["little", "big", "older"],
    medium: ["big", "older"],
    hard:   ["older"],
  },
  // Math Snap difficulty
  snap: {
    easy:   ["little", "big", "older"],
    medium: ["big", "older"],
    hard:   ["older"],
  },
  // Quiz mode
  quiz: {
    basic:    ["little", "big", "older"],
    advanced: ["big", "older"],
    expert:   ["older"],
  },
};

/**
 * Age never LOCKS content — children can browse and view every year, level
 * and topic regardless of the age they picked. Age is used only to set a
 * sensible DEFAULT/recommendation elsewhere, not to gate access.
 * (Product directive: lessons & learning must not be limited by age.)
 * Kept as a function so existing callers keep working; always returns false.
 */
export function isLockedForAge(/* ageId, game, key */) {
  return false;
}

// React hook — reactive to the cross-tab + same-page "we-age-changed" event.
export function useAgeGroup() {
  const [id, setId] = useState(() => getAgeGroup());
  useEffect(() => {
    const onChange = () => setId(getAgeGroup());
    window.addEventListener("we-age-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("we-age-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);
  return [id, setAgeGroup, getAgeMeta(id)];
}

/**
 * Convenience hook — returns a representative numeric age (5 / 8 / 12) for
 * the current age group, or `null` if the child hasn't picked one yet.
 * Used by curriculum content that needs a numeric age to select the right
 * age tier (e.g. `topicContent.js` tierForAge(age)).
 */
export function useAgeYears() {
  const [, , meta] = useAgeGroup();
  return meta?.defaultAge ?? null;
}

/**
 * Convenience hook — returns the current tier id ("seedlings" | "explorers"
 * | "pioneers") or null. Everything user-facing (badges, filters, streak
 * gating) should read this, NOT the legacy `little/big/older` id.
 */
export function useTier() {
  const [, , meta] = useAgeGroup();
  return meta?.tier ?? null;
}

/**
 * Content-gating hooks. Seedlings never see streaks or quiz CTAs — this is
 * a deliberate product principle (child-led wonder over comparison anxiety).
 * Components must check these BEFORE rendering streak flames, quiz cards,
 * "test yourself" buttons or completion badges.
 *
 * When no tier is selected yet we default to hiding streaks (safer) and
 * showing quizzes (parity with existing UX).
 */
export function useHasStreaks() {
  const [, , meta] = useAgeGroup();
  return meta?.hasStreaks ?? false;
}

export function useHasQuizzes() {
  const [, , meta] = useAgeGroup();
  return meta?.hasQuizzes ?? true;
}

// Convenience: returns true if the given tile key is recommended for the
// current age group. Always returns true when no age group is selected yet.
export function isRecommendedFor(ageId, tileKey) {
  if (!ageId) return false;
  const meta = getAgeMeta(ageId);
  return !!meta?.recommendedTiles?.includes(tileKey);
}
